/** One Resource command/receipt/authority lifetime shared by existing editors.
 * Kind adapters own content validation; this owner never persists Feedback. */
import { isCurrentDraftContext, type DraftContext } from '@/features/coach-review/composerDrafts';
import { ApiError, getCoachResourceRevision } from '@/lib/api';
import { bindResourceEditorReturn, completeResourceEditorLink, continueResourceEditorCommand, loadResourceEditorContinuation,
  resourceEditorContinuationKind, restoreResourceEditorCommand, type ResourceEditorReturnTarget, type ResourceEditorSelection } from '@/lib/resourceEditorContinuation';
import type { ResourceCommand, ResourceCommandResult, ResourceDetailResponse, ResourceReadScope, ResourceKind, SaveResourceRequest } from '@/lib/types';
import { useAuthStore } from '@/stores/auth';
import { useScopeStore } from '@/stores/scope';

export type ResourceEditorInitial<Value> = { kind:'new'; value:Value } | { kind:'saved'; detail:ResourceDetailResponse };
export type ResourceEditorAdapter<Value extends {label:string}> = {
  kind:ResourceKind;
  fromDetail(detail:ResourceDetailResponse):Value;
  fromCommand(command:ResourceCommand):Value|null;
  validate(value:Value):Value;
  changed(current:Value,baseline:Value):boolean;
  payload(value:Value):SaveResourceRequest['payload'];
  /** A redacted derived origin cannot be flattened into a new revision/copy. */
  canPreserveContent?(detail:ResourceDetailResponse|null):boolean;
};
type LinkOutcome = {status:'linked'|'already-linked'} | {status:'retained';reason:string};
export type ResourceEditorLinkTarget = {
  scope:Readonly<ResourceReadScope>;entryId:string;signal:AbortSignal;
  admit:(request:{kind:ResourceKind;creationAttemptId:string;receipt:ResourceCommandResult})=>Promise<LinkOutcome>;
};
const copy=<T>(value:T):T=>JSON.parse(JSON.stringify(value)) as T;
const changed=()=>new DOMException('Resource editor context changed','AbortError');
const required=()=>new Error('resource_editor_original_operation_required');

export function createResourceEditorSession<Value extends {label:string}>(context:DraftContext, entryId:string,
  initial:ResourceEditorInitial<Value>, adapter:ResourceEditorAdapter<Value>,
  returnTarget:ResourceEditorReturnTarget={kind:'standalone'}) {
  const scope=Object.freeze({...context});let target=copy(returnTarget);
  let working=copy(adapter.validate(initial.kind==='new' ? initial.value : adapter.fromDetail(initial.detail)));
  if(initial.kind==='saved'&&initial.detail.resource.kind!==adapter.kind)throw new Error('resource_editor_kind_mismatch');
  let baseline=copy(working);
  let origin:ResourceEditorSelection|null=initial.kind==='new' ? null : {
    resource_id:initial.detail.resource.resource_id, revision_id:initial.detail.resource.revision_id,
    version:initial.detail.resource.version,
  };
  let authority=initial.kind==='saved' ? copy(initial.detail) : null;
  let receipt:ResourceCommandResult|null=null, linkOutcome:LinkOutcome|null=null;
  let pending:ReturnType<typeof continueResourceEditorCommand>|null=null;
  let refreshRequired=false, refreshing=false, linking=false, active=true, retired=false, accessDenied=false;
  let contentHydrated=true;
  let generation=0, readAbort:AbortController|null=null;
  const listeners=new Set<()=>void>();
  const notify=()=>listeners.forEach(fn=>fn());
  const current=()=>!retired && isCurrentDraftContext(scope);
  const assertCurrent=()=>{if(!current())throw changed();};
  const depart=()=>{
    if (!isCurrentDraftContext(scope)) {retired=true;generation++;readAbort?.abort();notify();}
  };

  // Restoration is local and inert. Current GET metadata never changes a
  // captured pending origin or its original expected_version.
  const kept=loadResourceEditorContinuation(scope,entryId);
  if (kept) {
    if(resourceEditorContinuationKind(kept)!==adapter.kind)throw required();
    if (JSON.stringify(kept.return_target)!==JSON.stringify(target)) throw required();
    if (kept.pending) {
      pending=restoreResourceEditorCommand(scope,entryId);
      origin=copy(kept.origin);
      const submitted=adapter.fromCommand(kept.pending);
      contentHydrated=initial.kind==='saved'||submitted!==null;
      working=submitted ?? working;
      baseline=copy(working);
    } else if (kept.accepted) {
      receipt=copy(kept.accepted.receipt);
      origin={resource_id:receipt.resource_id,revision_id:receipt.revision_id,version:receipt.version};
      const submitted=adapter.fromCommand(kept.accepted.command);
      contentHydrated=initial.kind==='saved'||submitted!==null;
      working=submitted ?? working;
      baseline=copy(working);refreshRequired=true;
      // A prior local admission is not a durable Feedback save. A restored
      // composer must explicitly re-admit and inspect its current pins.
    }
  }
  const stopAuth=useAuthStore.subscribe(depart), stopScope=useScopeStore.subscribe(depart);
  depart();

  const dirty=()=>adapter.changed(working,baseline);
  const editor=()=>authority?.editor;
  const canPreserveContent=()=>adapter.canPreserveContent?.(authority)!==false;
  const canEdit=()=>canPreserveContent()&&(!origin || editor()?.can_edit===true
    && editor()?.current_revision_id===origin.revision_id);
  const canStart=()=>current() && active && contentHydrated && !accessDenied && !pending && !refreshRequired && !refreshing && !linking
    && !(receipt && target.kind!=='standalone');
  function assertNew(){assertCurrent();if(!canStart())throw required();}
  function assertMutable(){assertCurrent();if(!active || !contentHydrated || accessDenied || !canEdit() || receipt&&target.kind!=='standalone'&&!pending)throw new Error('resource_editor_read_only');}

  async function refresh():Promise<void> {
    assertCurrent();if(!active || !origin)throw required();
    readAbort?.abort();const abort=new AbortController();readAbort=abort;
    const selected=copy(origin), request=++generation;
    refreshing=true;refreshRequired=true;notify();
    try {
      const detail=await getCoachResourceRevision(selected,{scope,signal:abort.signal});
      assertCurrent();if(abort.signal.aborted || request!==generation)throw changed();
      if(detail.resource.resource_id!==selected.resource_id || detail.resource.revision_id!==selected.revision_id
        || detail.resource.kind!==adapter.kind || !detail.editor)throw new Error('resource_editor_invalid_response');
      if(!contentHydrated&&detail.resource.availability==='available'){
        // Metadata-only restored commands carry no drawing/source payload. Admit
        // the selected exact content once; never advance its frozen origin/CAS.
        working=copy(adapter.validate(adapter.fromDetail(detail)));baseline=copy(working);contentHydrated=true;
      }
      authority=copy(detail);accessDenied=detail.resource.availability!=='available';refreshRequired=false;
      // No form, baseline, selected revision or CAS rebasing from this GET.
    } catch(error) {
      if(request===generation && current() && error instanceof ApiError && [401,403,404].includes(error.status)) {
        accessDenied=true;authority=null;
      }
      throw error;
    } finally {
      if(request===generation){refreshing=false;notify();}
    }
  }

  async function execute():Promise<ResourceCommandResult> {
    assertCurrent();if(!active || !pending)throw required();
    const original=pending.getIntent();if(!original)throw changed();
    const controller=pending;notify();
    try {
      const sending=controller.send();notify();
      const result=await sending;
      assertCurrent();if(pending!==controller)throw required();
      receipt=copy(result);
      origin={resource_id:result.resource_id,revision_id:result.revision_id,version:result.version};
      const submitted=adapter.fromCommand(original);
      if(submitted)baseline=copy(submitted);
      // Do not reset working: edits made during the request survive its ACK.
      pending=null;refreshRequired=true;notify();
      return copy(result);
    } catch(error) {
      if(current() && error instanceof ApiError && [401,403,404].includes(error.status)) {
        accessDenied=true;authority=null;refreshRequired=true;
      }
      throw error;
    } finally {notify();}
  }
  function capture(command:ResourceCommand):Promise<ResourceCommandResult> {
    pending=continueResourceEditorCommand(scope,entryId,target,command,{origin,kind:adapter.kind});
    return execute();
  }

  return {
    entryId,scope,getReturnTarget:()=>current() ? copy(target) : null,
    subscribe(listener:()=>void){listeners.add(listener);return()=>{listeners.delete(listener);};},
    getState(){
      if(!current())return {status:'unavailable' as const};
      // Keep the original controller/receipt recoverable, but never return a
      // protected scene or label after a fresh denial/source-loss projection.
      if(accessDenied||!contentHydrated)return {status:'access-denied' as const,active,origin:copy(origin),receipt:copy(receipt),
        command:pending?.getState() ?? null,refreshing};
      return {status:'available' as const,kind:adapter.kind,active,value:copy(working),origin:copy(origin),dirty:dirty(),
        command:pending?.getState() ?? null,receipt:copy(receipt),linkOutcome:copy(linkOutcome),
        refreshRequired,refreshing,linking,authority:copy(authority),
        canEdit:canEdit()&&!(receipt&&target.kind!=='standalone'&&!pending),canSave:canStart() && canEdit(),
        canDuplicate:canStart() && canPreserveContent() && !dirty() && editor()?.can_duplicate===true,
        canArchive:canStart() && !dirty() && editor()?.can_archive===true};
    },
    setActive(value:boolean){assertCurrent();active=value;if(!value){generation++;readAbort?.abort();refreshing=false;}notify();},
    changeLabel(label:string){assertMutable();working=copy(adapter.validate({...working,label}));notify();},
    change(value:Value){
      assertMutable();working=copy(adapter.validate(value));notify();
    },
    save(operationId:string=crypto.randomUUID()){
      assertNew();if(!canEdit())throw new Error('resource_editor_read_only');
      const body={schema_version:'resource.v1' as const,operation_id:operationId,expected_version:origin?.version ?? 0,
        kind:adapter.kind,label:working.label,payload:adapter.payload(working)};
      return capture(origin ? {action:'save',resourceId:origin.resource_id,body} : {action:'create',body});
    },
    duplicate(operationId:string=crypto.randomUUID()){
      assertNew();if(!origin || !canPreserveContent() || dirty() || editor()?.can_duplicate!==true)throw new Error('resource_editor_read_only');
      return capture({action:'duplicate',resourceId:origin.resource_id,body:{schema_version:'resource-duplicate.v1',
        operation_id:operationId,expected_version:origin.version,revision_id:origin.revision_id}});
    },
    archive(operationId:string=crypto.randomUUID()){
      assertNew();if(!origin || dirty() || editor()?.can_archive!==true)throw new Error('resource_editor_read_only');
      return capture({action:'archive',resourceId:origin.resource_id,body:{schema_version:'resource-archive.v1',
        operation_id:operationId,expected_version:origin.version}});
    },
    retry:execute,refresh,
    withholdContent(){assertCurrent();accessDenied=true;authority=null;refreshRequired=true;notify();},
    beginFeedbackReturn(next:Extract<ResourceEditorReturnTarget,{kind:'feedback'}>){
      assertNew();if(!receipt || dirty() || target.kind!=='standalone')throw required();
      bindResourceEditorReturn(scope,entryId,next);target=copy(next);notify();
    },
    releaseRejected(){assertCurrent();if(!pending)throw required();pending.releaseRejected();pending=null;notify();},
    discardChanges(){assertCurrent();if(!active||accessDenied||pending||refreshing||linking)throw required();working=copy(baseline);notify();},
    async returnToFeedback(destination:ResourceEditorLinkTarget):Promise<LinkOutcome>{
      assertCurrent();if(!active || target.kind!=='feedback' || !receipt || pending || linking)throw required();
      if(destination.signal.aborted || destination.entryId!==target.entry_id
        || destination.scope.actorId!==scope.actorId || destination.scope.clubId!==scope.clubId
        || destination.scope.teamId!==scope.teamId || destination.scope.seasonId!==scope.seasonId)throw changed();
      const saved=copy(receipt), request=generation;linking=true;notify();
      try {
        // The host reactivates the ORIGINAL retained composer before calling.
        // Its exact admission reauthorizes; receipt/version is no access grant.
        const outcome=await destination.admit({kind:adapter.kind,creationAttemptId:target.creation_attempt_id,receipt:copy(saved)});
        if(destination.signal.aborted || outcome.status==='retained'&&['unavailable-resource','departed-target'].includes(outcome.reason)){
          accessDenied=true;authority=null;refreshRequired=true;
        }
        assertCurrent();if(!active || request!==generation || destination.signal.aborted)throw changed();
        if(outcome.status==='linked' || outcome.status==='already-linked')completeResourceEditorLink(scope,entryId,target,saved);
        linkOutcome=copy(outcome);return copy(outcome);
      } catch(error){
        if(destination.signal.aborted || error instanceof ApiError&&[401,403,404].includes(error.status)){
          accessDenied=true;authority=null;refreshRequired=true;
        }
        throw error;
      } finally {linking=false;notify();}
    },
    dispose(){retired=true;generation++;readAbort?.abort();stopAuth();stopScope();notify();listeners.clear();},
  };
}

export type ResourceEditorSession<Value extends {label:string}>=ReturnType<typeof createResourceEditorSession<Value>>;
